import { ref, reactive } from '@vue/composition-api'
import { PrinterConnection } from '@yonarezi/yonarezi3.epos-printer'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'

const STORAGE_KEY = 'kitchen_printer_enabled_ids'

function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

// シングルトン: 全てのcomposable呼び出しで共有
const enabledPrinterIds = ref(loadFromStorage())
const connectionStatuses = reactive({})
const isAutoDetecting = ref(false)

export default function useKitchenPrinterSelection() {
  function setEnabledPrinterIds(ids) {
    enabledPrinterIds.value = ids
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  }

  function isPrinterEnabled(printerId) {
    if (enabledPrinterIds.value === null) return true

    return enabledPrinterIds.value.includes(String(printerId))
  }

  function togglePrinter(printerId, allPrinterIds) {
    const id = String(printerId)

    // 未設定の場合、全プリンターIDから該当を除外したリストを作成
    if (enabledPrinterIds.value === null) {
      const ids = allPrinterIds.map(String).filter(pid => pid !== id)
      setEnabledPrinterIds(ids)

      return
    }

    if (enabledPrinterIds.value.includes(id)) {
      setEnabledPrinterIds(enabledPrinterIds.value.filter(pid => pid !== id))
    } else {
      setEnabledPrinterIds([...enabledPrinterIds.value, id])
    }
  }

  async function testConnection(printer) {
    const id = String(printer.id)
    connectionStatuses[id] = 'checking'

    try {
      const ipAddress = removePortFromIpAddress(printer.attributes.ipAddress)
      const conn = new PrinterConnection({ ipAddress })
      const p = await conn.connect()

      if (p?.ePosDev?.isConnected()) {
        connectionStatuses[id] = 'connected'
        conn.disconnect()

        return true
      }
      connectionStatuses[id] = 'failed'

      return false
    } catch {
      connectionStatuses[id] = 'failed'

      return false
    }
  }

  async function autoDetectPrinters(printers) {
    isAutoDetecting.value = true
    const enabledIds = []

    const results = await Promise.allSettled(
      printers.map(async printer => {
        const ok = await testConnection(printer)
        if (ok) enabledIds.push(String(printer.id))
      }),
    )

    // allSettledなので全て完了後に保存
    setEnabledPrinterIds(enabledIds)
    isAutoDetecting.value = false

    return results
  }

  // 有効なプリンターが1台以上あるか判定
  function hasAnyEnabled(printers) {
    if (!printers || printers.length === 0) return false
    // 未設定(null)は全てON扱い → true
    if (enabledPrinterIds.value === null) return true

    return printers.some(p => enabledPrinterIds.value.includes(String(p.id)))
  }

  // localStorage未設定かどうか
  function isFirstTime() {
    return loadFromStorage() === null
  }

  return {
    enabledPrinterIds,
    connectionStatuses,
    isAutoDetecting,
    isPrinterEnabled,
    togglePrinter,
    testConnection,
    autoDetectPrinters,
    hasAnyEnabled,
    isFirstTime,
  }
}
